'use client';

import React, { useState, useRef, useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { Mail, Eye, EyeOff } from 'lucide-react';
import { type Country } from 'react-phone-number-input';
import { toDialCode } from '@/lib/utils';
import { studioPhoneCountry } from '@/lib/phone-country';
import { Modal, Input, Link, Button, Avatar } from '@mokoboko/components';
import SmartPhoneInput from '@/components/smart-phone-input';
import OtpInputGroup from '@/components/otp-input-group';
import { ALLOWED_COUNTRIES, GENERIC_ERROR_MESSAGE } from '@/lib/constants';
import {
  signupSchema,
  otpSchema,
  type SignupFormValues,
  type OtpFormValues,
} from '@/lib/schemas';
import { useOtpInput } from '@/hooks/use-otp-input';
import { PatternModalHeader } from '@/components/dialogs/pattern-modal-header';

import { createAccount, verifySignupOtp } from '@/services/auth';
import { User, setSignedInUser } from '@/state/user';
import useTenant from '@/state/tenant';

type Step = 'signup' | 'otp';

interface CreateAccountDialogProps {
  email?: string;
  onClose: VoidFunction;
  onConfirm: (user: User) => Promise<void>;
  onLoginLinkClick: VoidFunction;
  open: boolean;
  showLoginLink?: boolean;
}

export const CreateAccountDialog = ({
  email,
  onClose,
  onConfirm,
  onLoginLinkClick,
  open,
  showLoginLink = true,
}: CreateAccountDialogProps) => {
  const { data: tenant } = useTenant();
  const studioCountry = studioPhoneCountry(tenant);
  const queryClient = useQueryClient();
  const otp = useOtpInput();

  const [step, setStep] = useState<Step>('signup');
  const [isProcessing, setIsProcessing] = useState(false);
  const [formError, setFormError] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [showRepeatPassword, setShowRepeatPassword] = useState(false);
  const [shake, setShake] = useState(0);

  const signupPayloadRef = useRef<{
    first_name: string;
    last_name: string;
    email: string;
    password: string;
    phone: string;
    country_code: string;
  } | null>(null);
  const logoUrl = tenant?.logo || '/images/logo.png';

  const signupForm = useForm<SignupFormValues>({
    resolver: zodResolver(signupSchema),
    defaultValues: { country_code: studioCountry, phone: '' },
  });

  const phoneCountry =
    (useWatch({ control: signupForm.control, name: 'country_code' }) as Country) ||
    studioCountry;
  const phoneValue = useWatch({ control: signupForm.control, name: 'phone' }) || '';

  useEffect(() => {
    signupForm.register('phone');
    signupForm.register('country_code');
  }, [signupForm.register]);

  const otpForm = useForm<OtpFormValues>({
    resolver: zodResolver(otpSchema),
  });

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  useEffect(() => {
    if (step === 'otp') {
      setTimeout(() => otp.focusFirst(), 300);
    }
  }, [step]);

  const isProcessingRef = useRef(false);
  isProcessingRef.current = isProcessing;

  useEffect(() => {
    otpForm.setValue('otp', otp.combined, {
      shouldValidate: otp.combined.length === 6,
    });
    if (otp.combined.length === 6 && !isProcessingRef.current) {
      onOtpSubmit({ otp: otp.combined });
    }
  }, [otp.combined, otpForm]);

  const onSignupSubmit = async (data: SignupFormValues) => {
    try {
      setIsProcessing(true);
      setFormError('');

      const callingCode = toDialCode(data.country_code);
      const payload: typeof signupPayloadRef.current = {
        first_name: data.first_name.trim(),
        last_name: data.last_name.trim(),
        email: data.email.toLowerCase().trim(),
        password: data.password,
        phone: data.phone,
        country_code: callingCode,
      };
      await createAccount(payload);

      signupPayloadRef.current = payload;
      setUserEmail(payload.email);
      setStep('otp');
      setResendCooldown(60);
    } catch (error: any) {
      setFormError(
        error?.message || GENERIC_ERROR_MESSAGE,
      );
    } finally {
      setIsProcessing(false);
    }
  };

  const onOtpSubmit = async (data: OtpFormValues) => {
    try {
      setIsProcessing(true);
      setFormError('');

      const response = await verifySignupOtp({
        email: userEmail,
        otp: data.otp,
      });

      if (response?.user) {
        setSignedInUser(queryClient, response.user);
        await onConfirm(response.user);
        resetAllForms();
      }
    } catch (error: any) {
      setFormError(error?.message || 'Invalid code. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendCooldown > 0 || !signupPayloadRef.current) return;
    try {
      setFormError('');
      await createAccount(signupPayloadRef.current);
      setResendCooldown(60);
      otp.reset();
      otp.focusFirst();
    } catch (error: any) {
      setFormError(
        error?.message || 'Failed to resend code. Please try again.',
      );
    }
  };

  const resetAllForms = () => {
    signupForm.reset();
    otpForm.reset();
    otp.reset();
    setStep('signup');
    setFormError('');
    setUserEmail('');
    setShowPassword(false);
    setShowRepeatPassword(false);
    signupPayloadRef.current = null;
  };

  const handleBack = () => {
    setStep('signup');
    otp.reset();
    setFormError('');
    otpForm.reset();
  };

  return (
    <Modal.Root
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          resetAllForms();
          onClose();
        }
      }}
    >
      <Modal.Content size="sm" hasCloseButton isPreventClose={isProcessing}>
        <PatternModalHeader className="sm:px-8 sm:py-8">
          <Avatar
            src={logoUrl}
            name={tenant?.name || 'MB'}
            size="xl"
            className="relative bg-pine-600 text-white"
          />

          <Modal.Title className="relative mt-4 text-gray-900">
            {step === 'signup'
              ? 'Create an account'
              : 'Enter verification code'}
          </Modal.Title>
          <Modal.Description className="relative mt-1 max-w-sm text-gray-700">
            {step === 'signup' ? (
              'With an account you can checkout faster and manage all your classes and packs in one place!'
            ) : (
              <>
                We sent a 6-digit code to{' '}
                <span className="font-medium text-gray-900">{userEmail}</span>
              </>
            )}
          </Modal.Description>
        </PatternModalHeader>

        <Modal.Body className="flex flex-col gap-6 overflow-visible p-5 pt-0 pb-8 sm:px-8 sm:pb-8">
          {step === 'signup' && (
            <form
              className="space-y-5"
              onSubmit={signupForm.handleSubmit(onSignupSubmit, () => setShake(s => s + 1))}
            >
              {formError && (
                <p className="text-sm leading-5 text-error-500">{formError}</p>
              )}

              <div className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-2 sm:gap-5">
                  <Input
                    {...signupForm.register('first_name')}
                    hasError={!!signupForm.formState.errors?.first_name}
                    errorMessage={
                      signupForm.formState.errors?.first_name?.message
                    }
                    shake={signupForm.formState.errors?.first_name ? shake : 0}
                    placeholder="First name"
                  />
                  <Input
                    {...signupForm.register('last_name')}
                    hasError={!!signupForm.formState.errors?.last_name}
                    errorMessage={
                      signupForm.formState.errors?.last_name?.message
                    }
                    shake={signupForm.formState.errors?.last_name ? shake : 0}
                    placeholder="Last name"
                  />
                </div>

                <Input
                  {...signupForm.register('email')}
                  defaultValue={email || ''}
                  hasError={!!signupForm.formState.errors?.email}
                  errorMessage={signupForm.formState.errors?.email?.message}
                  shake={signupForm.formState.errors?.email ? shake : 0}
                  placeholder="Email"
                />

                <SmartPhoneInput
                  countries={ALLOWED_COUNTRIES}
                  country={phoneCountry}
                  onCountryChange={(c) => {
                    signupForm.setValue('country_code', c, { shouldDirty: true });
                    signupForm.setValue('phone', '', { shouldDirty: true });
                  }}
                  phone={phoneValue}
                  onPhoneChange={(v) => {
                    signupForm.setValue('phone', v, { shouldDirty: true });
                  }}
                  error={!!signupForm.formState.errors?.phone}
                  errorText={signupForm.formState.errors?.phone?.message}
                  showLabel={false}
                  usePortal={false}
                />

                <Input
                  {...signupForm.register('password')}
                  hasError={!!signupForm.formState.errors?.password}
                  errorMessage={signupForm.formState.errors?.password?.message}
                  shake={signupForm.formState.errors?.password ? shake : 0}
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  placeholder="Password"
                  trailingIcon={showPassword ? EyeOff : Eye}
                  onTrailingIconClick={() => setShowPassword((v) => !v)}
                  trailingIconAriaLabel="Toggle password visibility"
                />

                <Input
                  {...signupForm.register('repeatPassword')}
                  hasError={!!signupForm.formState.errors?.repeatPassword}
                  errorMessage={
                    signupForm.formState.errors?.repeatPassword?.message
                  }
                  shake={signupForm.formState.errors?.repeatPassword ? shake : 0}
                  type={showRepeatPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  placeholder="Confirm password"
                  trailingIcon={showRepeatPassword ? EyeOff : Eye}
                  onTrailingIconClick={() => setShowRepeatPassword((v) => !v)}
                  trailingIconAriaLabel="Toggle password visibility"
                />
              </div>

              <Button
                fullWidth
                isProcessing={isProcessing}
                type="submit"
                size="lg"
              >
                {isProcessing ? 'Sending code…' : 'Create account'}
              </Button>

              <div className="border-t border-gray-200" />

              {showLoginLink && (
                <div className="text-center">
                  <Link
                    color="red"
                    size="md"
                    className="sm:!text-sm sm:!leading-5"
                    onClick={(e) => {
                      e.preventDefault();
                      onLoginLinkClick();
                    }}
                    role="button"
                    tabIndex={0}
                  >
                    Have an account? Log in
                  </Link>
                </div>
              )}
            </form>
          )}

          {step === 'otp' && (
            <form
              className="space-y-5"
              onSubmit={otpForm.handleSubmit(onOtpSubmit)}
            >
              <div className="space-y-4">
                <div className="flex items-center justify-center gap-2 rounded-lg bg-gray-50 px-3 py-2">
                  <Mail className="h-4 w-4 text-gray-500" />
                  <span className="text-sm text-gray-600">
                    Code sent via email
                  </span>
                </div>

                <OtpInputGroup
                  otp={otp}
                  hasError={!!otpForm.formState.errors?.otp}
                  errorMessage={otpForm.formState.errors?.otp?.message}
                />
              </div>

              <Button
                fullWidth
                isProcessing={isProcessing}
                type="submit"
                size="lg"
              >
                {isProcessing ? 'Verifying…' : 'Verify & create account'}
              </Button>

              {formError && (
                <p className="text-sm leading-5 text-error-500">{formError}</p>
              )}

              <div className="border-t border-gray-200" />

              <div className="flex items-center justify-center gap-3">
                <Button
                  type="button"
                  variant="link-color"
                  size="sm"
                  onClick={handleResendOtp}
                  disabled={resendCooldown > 0}
                >
                  {resendCooldown > 0
                    ? `Resend code in ${resendCooldown}s`
                    : 'Resend code'}
                </Button>

                <span className="h-4 w-px bg-gray-300" aria-hidden />

                <Link
                  color="red"
                  size="sm"
                  className="text-sm leading-5"
                  onClick={(e) => {
                    e.preventDefault();
                    handleBack();
                  }}
                  role="button"
                  tabIndex={0}
                >
                  Go back
                </Link>
              </div>
            </form>
          )}
        </Modal.Body>
      </Modal.Content>
    </Modal.Root>
  );
};
